'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, CreditCard, Upload, FileText, IdCard, CheckCircle2, AlertCircle, Loader2, ChevronRight, Lock, Eye, EyeOff, Shield, Briefcase, GraduationCap } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { isInFreeWindow, FREE_UNTIL } from '@/lib/freeSignupWindow';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  idNumber: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  streetAddress: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
  jobType: string;
  salaryMin: string;
  salaryMax: string;
  yearsExperience: string;
  preferredRegion: string;
  hasPassport: string;
  criminalRecord: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

const provinces = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Western Cape',
];

const southAfricaJobTypes = [
  'General Worker',
  'Security Guard / Officer',
  'Cleaner / Domestic Worker',
  'Cashier / Retail Assistant',
  'Warehouse / Logistics Worker',
  'Construction Worker / Builder',
  'Petrol Attendant',
  'Factory / Production Worker',
  'Gardener / Landscaper',
  'Caregiver / Home-Based Care',
  'Call Centre Agent',
  'Data Capturer / Admin Clerk',
  'Receptionist',
  'Delivery Driver',
  'Forklift Operator',
  'Electrician / Artisan',
  'Plumber',
  'Welder / Boilermaker',
  'Mechanic / Auto Technician',
  'IT Support Technician',
  'Nurse / Healthcare Assistant',
  'Teacher / Educator',
  'Accountant / Bookkeeper',
  'Sales Representative',
  'Chef / Cook',
  'Waiter / Hospitality Staff',
  'Security Supervisor',
  'Supervisor / Team Leader',
  'Store Manager',
  'Project Manager',
];

interface UploadedFile {
  name: string;
  size: number;
  type: string;
}

export default function SignUpForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{
    cv: UploadedFile | null;
    license: UploadedFile | null;
    id: UploadedFile | null;
    qualification: UploadedFile | null;
  }>({ cv: null, license: null, id: null, qualification: null });
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  const searchParams = useSearchParams();
  const supabase = createClient();

  useEffect(() => {
    const jobId = searchParams.get('apply_job');
    if (jobId) {
      setPendingJobId(jobId);
      toast.info('Sign up & pay to apply for this job automatically!', { duration: 5000 });
    }
  }, [searchParams]);

  const cvRef = useRef<HTMLInputElement>(null);
  const licenseRef = useRef<HTMLInputElement>(null);
  const idRef = useRef<HTMLInputElement>(null);
  const qualificationRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<SignUpFormData>({ mode: 'onBlur' });

  const steps = [
    { key: 'step-1', number: 1, label: 'Personal Details' },
    { key: 'step-2', number: 2, label: 'Job Preferences' },
    { key: 'step-3', number: 3, label: 'Documents' },
    { key: 'step-4', number: 4, label: 'Account & Payment' },
  ];

  const handleFileUpload = (
    field: 'cv' | 'license' | 'id' | 'qualification',
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['application/pdf', 'image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Only PDF, PNG, or JPG files are accepted.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB.');
      return;
    }
    setUploadedFiles((prev) => ({
      ...prev,
      [field]: { name: file.name, size: file.size, type: file.type },
    }));
    toast.success(`${file.name} uploaded successfully.`);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleNextStep = async () => {
    let fieldsToValidate: (keyof SignUpFormData)[] = [];
    if (currentStep === 1) {
      fieldsToValidate = [
        'firstName', 'lastName', 'email', 'phone', 'idNumber',
        'dateOfBirth', 'streetAddress', 'city', 'province', 'postalCode',
      ];
    } else if (currentStep === 2) {
      fieldsToValidate = ['jobType', 'yearsExperience'];
    }
    const valid = await trigger(fieldsToValidate);
    if (valid) setCurrentStep((s) => Math.min(s + 1, 4));
  };

  const onSubmit = async (data: SignUpFormData) => {
    if (!uploadedFiles.cv || !uploadedFiles.id) {
      toast.error('Please upload your CV and ID document before proceeding.');
      return;
    }
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);

    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: `${data.firstName} ${data.lastName}`,
            job_type: data.jobType,
            salary_min: parseInt(data.salaryMin || '0', 10),
            salary_max: parseInt(data.salaryMax || '999999', 10),
            preferred_region: data.preferredRegion || '',
          },
        },
      });

      if (signUpError) {
        toast.error(signUpError.message || 'Registration failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const paymentId = `JOBFINDER-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

      const { error: dbError } = await supabase
        .from('payment_registrations')
        .insert({
          payment_id: paymentId,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          amount: 92.00,
          payment_status: 'pending',
          pending_job_id: pendingJobId || null,
        });

      if (dbError) {
        console.error('Failed to store payment record:', dbError);
      }

      // ── FREE TESTING WINDOW: bypass PayFast if within 15-hour window ──
      if (isInFreeWindow()) {
        await supabase.from('payment_registrations').update({ payment_status: 'free_testing' }).eq('payment_id', paymentId);
        toast.success('Account created! Welcome to Job Finder — free testing period active.');
        const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
        window.location.href = `${siteUrl}/user-dashboard`;
        return;
      }
      // ── END FREE WINDOW ──

      toast.success('Account created! Redirecting to secure payment...');

      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;

      const pfResponse = await fetch('/api/payfast/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          paymentId,
          pendingJobId: pendingJobId || null,
          siteUrl,
        }),
      });

      if (!pfResponse.ok) {
        toast.error('Payment setup failed. Please contact support.');
        setIsSubmitting(false);
        return;
      }

      const { fields, action } = await pfResponse.json();

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action;

      Object.entries(fields as Record<string, string>).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error('Registration error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl card-shadow-md border border-border overflow-hidden">
      {/* Pending job banner */}
      {pendingJobId && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center gap-2">
          <Briefcase size={15} className="text-amber-600 shrink-0" />
          <p className="text-amber-800 text-xs font-600">
            You&apos;re applying for job <span className="font-800 uppercase">{pendingJobId}</span> — complete sign up &amp; payment to auto-apply.
          </p>
        </div>
      )}
      {/* Step Progress */}
      <div className="bg-secondary/50 px-6 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          {steps.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 transition-colors ${
                    currentStep > step.number
                      ? 'bg-green-500 text-white'
                      : currentStep === step.number
                      ? 'bg-primary text-white' :'bg-border text-muted-foreground'
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircle2 size={14} />
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={`text-xs font-600 hidden sm:block ${
                    currentStep === step.number ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 transition-colors ${
                    currentStep > step.number ? 'bg-green-400' : 'bg-border'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="px-6 py-6">
          {/* ─── STEP 1: Personal Details ─── */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-700 text-foreground">Personal Details</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Your basic personal information for your job seeker profile.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('firstName', { required: 'First name is required' })}
                    className={`input-field ${errors.firstName ? 'error' : ''}`}
                    placeholder="e.g. Sipho"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.firstName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('lastName', { required: 'Last name is required' })}
                    className={`input-field ${errors.lastName ? 'error' : ''}`}
                    placeholder="e.g. Ndlovu"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                      })}
                      className={`input-field pl-9 ${errors.email ? 'error' : ''}`}
                      placeholder="sipho@email.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="tel"
                      {...register('phone', {
                        required: 'Phone number is required',
                        pattern: { value: /^[0-9+\s-]{10,15}$/, message: 'Enter a valid SA number' },
                      })}
                      className={`input-field pl-9 ${errors.phone ? 'error' : ''}`}
                      placeholder="e.g. 082 456 7890"
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.phone.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    SA ID Number <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">13-digit South African ID number</p>
                  <input
                    {...register('idNumber', {
                      required: 'ID number is required',
                      pattern: { value: /^\d{13}$/, message: 'Must be 13 digits' },
                    })}
                    className={`input-field ${errors.idNumber ? 'error' : ''}`}
                    placeholder="e.g. 8501015009087"
                    maxLength={13}
                  />
                  {errors.idNumber && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.idNumber.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...register('dateOfBirth', { required: 'Date of birth is required' })}
                    className={`input-field ${errors.dateOfBirth ? 'error' : ''}`}
                  />
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.dateOfBirth.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">Gender</label>
                  <select {...register('gender')} className="input-field">
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer-not">Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">Nationality</label>
                  <input
                    {...register('nationality')}
                    className="input-field"
                    placeholder="e.g. South African"
                    defaultValue="South African"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                  <MapPin size={15} className="text-primary" /> Residential Address
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">
                      Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('streetAddress', { required: 'Street address is required' })}
                      className={`input-field ${errors.streetAddress ? 'error' : ''}`}
                      placeholder="e.g. 12 Mandela Drive"
                    />
                    {errors.streetAddress && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={11} /> {errors.streetAddress.message}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1.5">Suburb</label>
                      <input {...register('suburb')} className="input-field" placeholder="e.g. Soweto" />
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1.5">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('city', { required: 'City is required' })}
                        className={`input-field ${errors.city ? 'error' : ''}`}
                        placeholder="e.g. Johannesburg"
                      />
                      {errors.city && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle size={11} /> {errors.city.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1.5">
                        Province <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('province', { required: 'Province is required' })}
                        className={`input-field ${errors.province ? 'error' : ''}`}
                      >
                        <option value="">Select province</option>
                        {provinces.map((p) => (
                          <option key={`province-${p}`} value={p}>{p}</option>
                        ))}
                      </select>
                      {errors.province && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle size={11} /> {errors.province.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1.5">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('postalCode', {
                          required: 'Postal code is required',
                          pattern: { value: /^\d{4}$/, message: '4-digit SA postal code' },
                        })}
                        className={`input-field ${errors.postalCode ? 'error' : ''}`}
                        placeholder="e.g. 1801"
                        maxLength={4}
                      />
                      {errors.postalCode && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle size={11} /> {errors.postalCode.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 2: Job Preferences ─── */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-700 text-foreground">Job Preferences</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Tell us what type of work you are looking for.
                </p>
              </div>

              {/* Job Type */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">
                  Job Type <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Select the type of job you are looking for</p>
                <select
                  {...register('jobType', { required: 'Please select a job type' })}
                  className={`input-field ${errors.jobType ? 'error' : ''}`}
                >
                  <option value="">Select job type</option>
                  {southAfricaJobTypes.map((j) => (
                    <option key={`job-${j}`} value={j}>{j}</option>
                  ))}
                </select>
                {errors.jobType && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={11} /> {errors.jobType.message}
                  </p>
                )}
              </div>

              {/* Salary Range */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">
                  Expected Salary Range (ZAR per month)
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Jobs outside this range will be skipped during auto-apply
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Minimum (R)</label>
                    <input
                      type="number"
                      min="0"
                      step="500"
                      {...register('salaryMin')}
                      className="input-field"
                      placeholder="e.g. 5000"
                      defaultValue="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Maximum (R)</label>
                    <input
                      type="number"
                      min="0"
                      step="500"
                      {...register('salaryMax')}
                      className="input-field"
                      placeholder="e.g. 20000"
                      defaultValue="999999"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Leave at 0 / 999999 to apply to all salary ranges.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Years of Work Experience <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('yearsExperience', { required: 'Years of experience is required' })}
                    className={`input-field ${errors.yearsExperience ? 'error' : ''}`}
                  >
                    <option value="">Select years</option>
                    <option value="0">No experience (entry level)</option>
                    <option value="1">1 year</option>
                    <option value="2">2 years</option>
                    <option value="3">3 years</option>
                    <option value="4">4 years</option>
                    <option value="5">5 years</option>
                    <option value="6">6 years</option>
                    <option value="7">7 years</option>
                    <option value="8">8 years</option>
                    <option value="9">9 years</option>
                    <option value="10">10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                  {errors.yearsExperience && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.yearsExperience.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Preferred Work Region
                  </label>
                  <select {...register('preferredRegion')} className="input-field">
                    <option value="">No preference</option>
                    <option value="sa-only">South Africa Only</option>
                    <option value="international-only">International Only</option>
                    <option value="both">Both SA & International</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Do you have a valid passport?
                  </label>
                  <select {...register('hasPassport')} className="input-field">
                    <option value="">Select</option>
                    <option value="yes">Yes — valid passport</option>
                    <option value="no">No passport</option>
                    <option value="expired">Passport expired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Criminal Record Declaration <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('criminalRecord', { required: 'Please declare criminal record status' })}
                    className={`input-field ${errors.criminalRecord ? 'error' : ''}`}
                  >
                    <option value="">Select</option>
                    <option value="clear">I have a clear criminal record</option>
                    <option value="pending">Criminal record check pending</option>
                    <option value="previous">Previous convictions</option>
                  </select>
                  {errors.criminalRecord && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.criminalRecord.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 3: Documents ─── */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-700 text-foreground">Document Uploads</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Upload your documents as PDF, PNG or JPG. Maximum 10MB per file.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  CV and ID are required. Driver&apos;s license, PDP and qualification certificate are optional but improve your chances.
                </p>
              </div>

              {/* CV Upload — Required */}
              <div>
                <label className="block text-sm font-700 text-foreground mb-1.5">
                  Curriculum Vitae (CV) <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">Your professional CV — PDF or image format</p>
                <input
                  ref={cvRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(e) => handleFileUpload('cv', e)}
                />
                {uploadedFiles.cv ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-600 text-green-800 truncate">{uploadedFiles.cv.name}</p>
                      <p className="text-xs text-green-600">{formatFileSize(uploadedFiles.cv.size)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={18} className="text-green-500" />
                      <button
                        type="button"
                        onClick={() => setUploadedFiles((p) => ({ ...p, cv: null }))}
                        className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => cvRef.current?.click()}
                    className="upload-zone w-full p-6 flex flex-col items-center gap-2 text-center"
                  >
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                      <Upload size={22} className="text-primary" />
                    </div>
                    <p className="text-sm font-600 text-foreground">Click to upload your CV</p>
                    <p className="text-xs text-muted-foreground">PDF, PNG or JPG — max 10MB</p>
                  </button>
                )}
              </div>

              {/* ID Document Upload — Required */}
              <div>
                <label className="block text-sm font-700 text-foreground mb-1.5">
                  Identity Document (ID) <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  South African green ID book, smart ID card, or passport
                </p>
                <input
                  ref={idRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(e) => handleFileUpload('id', e)}
                />
                {uploadedFiles.id ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <IdCard size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-600 text-green-800 truncate">{uploadedFiles.id.name}</p>
                      <p className="text-xs text-green-600">{formatFileSize(uploadedFiles.id.size)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={18} className="text-green-500" />
                      <button
                        type="button"
                        onClick={() => setUploadedFiles((p) => ({ ...p, id: null }))}
                        className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => idRef.current?.click()}
                    className="upload-zone w-full p-6 flex flex-col items-center gap-2 text-center"
                  >
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                      <IdCard size={22} className="text-primary" />
                    </div>
                    <p className="text-sm font-600 text-foreground">Click to upload ID Document</p>
                    <p className="text-xs text-muted-foreground">PDF, PNG or JPG — max 10MB</p>
                  </button>
                )}
              </div>

              {/* Driver's License Upload — Optional */}
              <div>
                <label className="block text-sm font-700 text-foreground mb-1.5">
                  Driver&apos;s License &amp; PDP{' '}
                  <span className="text-xs font-400 text-muted-foreground">(Optional)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Upload a scan of your driver&apos;s license and/or PDP card — PDF or image
                </p>
                <input
                  ref={licenseRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(e) => handleFileUpload('license', e)}
                />
                {uploadedFiles.license ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CreditCard size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-600 text-green-800 truncate">{uploadedFiles.license.name}</p>
                      <p className="text-xs text-green-600">{formatFileSize(uploadedFiles.license.size)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={18} className="text-green-500" />
                      <button
                        type="button"
                        onClick={() => setUploadedFiles((p) => ({ ...p, license: null }))}
                        className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => licenseRef.current?.click()}
                    className="upload-zone w-full p-5 flex flex-col items-center gap-2 text-center border-dashed"
                  >
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                      <CreditCard size={18} className="text-primary" />
                    </div>
                    <p className="text-sm font-600 text-foreground">Click to upload License / PDP</p>
                    <p className="text-xs text-muted-foreground">Optional — PDF, PNG or JPG — max 10MB</p>
                  </button>
                )}
              </div>

              {/* Qualification Certificate Upload — Optional */}
              <div>
                <label className="block text-sm font-700 text-foreground mb-1.5">
                  Qualification Certificate{' '}
                  <span className="text-xs font-400 text-muted-foreground">(Optional)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Matric certificate, diploma, degree or any relevant qualification — PDF or image
                </p>
                <input
                  ref={qualificationRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(e) => handleFileUpload('qualification', e)}
                />
                {uploadedFiles.qualification ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <GraduationCap size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-600 text-green-800 truncate">{uploadedFiles.qualification.name}</p>
                      <p className="text-xs text-green-600">{formatFileSize(uploadedFiles.qualification.size)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={18} className="text-green-500" />
                      <button
                        type="button"
                        onClick={() => setUploadedFiles((p) => ({ ...p, qualification: null }))}
                        className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => qualificationRef.current?.click()}
                    className="upload-zone w-full p-5 flex flex-col items-center gap-2 text-center border-dashed"
                  >
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                      <GraduationCap size={18} className="text-primary" />
                    </div>
                    <p className="text-sm font-600 text-foreground">Click to upload Qualification Certificate</p>
                    <p className="text-xs text-muted-foreground">Optional — PDF, PNG or JPG — max 10MB</p>
                  </button>
                )}
              </div>

              <div className="bg-secondary rounded-xl p-4 text-xs text-muted-foreground space-y-1">
                <p className="font-600 text-foreground text-sm">Document Security</p>
                <p>All documents are encrypted and stored securely. They are only shared with employers once you are shortlisted for a position. Job Finder complies with POPIA (Protection of Personal Information Act).</p>
              </div>
            </div>
          )}

          {/* ─── STEP 4: Account & Payment ─── */}
          {currentStep === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-700 text-foreground">Account Setup & Payment</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {isInFreeWindow() ? 'Create your login credentials — registration is free during the testing period.' : 'Create your login credentials and complete the R92 registration fee.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters</p>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Minimum 8 characters' },
                      })}
                      className={`input-field pl-9 pr-10 ${errors.password ? 'error' : ''}`}
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.password.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register('confirmPassword', { required: 'Please confirm your password' })}
                      className={`input-field pl-9 pr-10 ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Summary Card — hidden during free window */}
              {isInFreeWindow() ? (
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-white text-xs font-bold uppercase tracking-wider">🎉 Free Testing Period</p>
                      <p className="text-4xl font-800 tabular-nums mt-1 text-white">FREE</p>
                      <p className="text-green-100 text-xs mt-1">No payment required — testing window active</p>
                    </div>
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                      <CheckCircle2 size={32} className="text-white" />
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-white/20 pt-4">
                    {[
                      'Lifetime access to Job Finder platform',
                      'Automatic applications to all matching jobs',
                      'SA domestic & international job coverage',
                      'Profile visible to 480+ partner companies',
                      'Real-time application status tracking',
                    ].map((item, i) => (
                      <div key={`fee-item-${i}`} className="flex items-center gap-2 text-sm text-green-100">
                        <CheckCircle2 size={14} className="text-green-300 shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-white/10 rounded-xl p-3 flex items-center gap-2">
                    <Shield size={14} className="text-green-200 shrink-0" />
                    <p className="text-xs text-green-200">
                      Free until {FREE_UNTIL.toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', dateStyle: 'medium', timeStyle: 'short' })} — R92 fee resumes after
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-white text-xs font-bold uppercase tracking-wider">Registration Fee</p>
                      <p className="text-4xl font-800 tabular-nums mt-1 text-white">R92</p>
                      <p className="text-blue-200 text-xs mt-1">One-time payment — no monthly fees</p>
                    </div>
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                      <CreditCard size={32} className="text-white" />
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-white/20 pt-4">
                    {[
                      'Lifetime access to Job Finder platform',
                      'Automatic applications to all matching jobs',
                      'SA domestic & international job coverage',
                      'Profile visible to 480+ partner companies',
                      'Real-time application status tracking',
                    ].map((item, i) => (
                      <div key={`fee-item-${i}`} className="flex items-center gap-2 text-sm text-blue-100">
                        <CheckCircle2 size={14} className="text-blue-300 shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-white/10 rounded-xl p-3 flex items-center gap-2">
                    <Lock size={14} className="text-blue-200 shrink-0" />
                    <p className="text-xs text-blue-200">
                      Secure payment via PayFast — SA&apos;s trusted payment gateway
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 font-700 text-sm mb-1">What happens after {isInFreeWindow() ? 'sign up' : 'payment'}?</p>
                <p className="text-blue-700 text-xs leading-relaxed">
                  {isInFreeWindow()
                    ? 'Job Finder immediately activates your profile and begins automatically applying to all available vacancies matching your job type and credentials. You will receive email notifications when employers respond.'
                    : 'After successful payment, Job Finder immediately activates your profile and begins automatically applying to all available vacancies matching your job type and credentials. You will receive email notifications when employers respond.'}
                </p>
              </div>

              {/* T&Cs Checkbox */}
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
                <p className="text-amber-800 font-700 text-sm flex items-center gap-2">
                  <Shield size={15} className="text-amber-600" />
                  Employment Agency Agreement — Please Read
                </p>
                <div className="bg-white border border-amber-200 rounded-lg p-3 text-xs text-gray-700 leading-relaxed max-h-32 overflow-y-auto space-y-1.5">
                  <p><strong>Agency Fee:</strong> By registering, you agree that upon successful job placement facilitated by Job Finder, <strong>30% (thirty percent) of your gross monthly remuneration</strong> will be deducted as an agency fee for a period of <strong>12 (twelve) consecutive months</strong> from your employment start date.</p>
                  <p><strong>Scope:</strong> This fee applies to all employment types — permanent, fixed-term, contract, temporary, part-time, and international placements. It is not limited to permanent employment.</p>
                  <p><strong>Governing Law:</strong> This agreement is governed by the Labour Relations Act 66 of 1995, the Basic Conditions of Employment Act 75 of 1997, and the Employment Services Act 4 of 2014 of the Republic of South Africa.</p>
                  <p><strong>Post-12 Months:</strong> After the 12-month period, no further agency fees will be deducted and your full salary will be paid directly by the employer.</p>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms"
                    {...register('termsAccepted', { required: 'You must accept the Terms & Conditions and Employment Agency Agreement to proceed' })}
                    className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-700 leading-relaxed">
                    I have read and agree to the{' '}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary font-600 hover:underline"
                    >
                      Terms &amp; Conditions
                    </a>{' '}
                    and the <strong>Employment Agency Agreement</strong>, including the 30% agency fee deduction for 12 months upon placement. I understand {isInFreeWindow() ? 'that Job Finder will apply to jobs on my behalf.' : 'the R92 registration fee is non-refundable and that Job Finder will apply to jobs on my behalf.'}
                  </label>
                </div>
                {errors.termsAccepted && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <AlertCircle size={11} /> {errors.termsAccepted.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer: Navigation Buttons */}
        <div className="px-6 py-4 bg-secondary/30 border-t border-border flex items-center justify-between">
          <button
            type="button"
            onClick={() => setCurrentStep((s) => Math.max(s - 1, 1))}
            disabled={currentStep === 1}
            className="text-sm font-600 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors px-4 py-2 rounded-lg hover:bg-secondary"
          >
            ← Back
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="btn-primary text-sm"
            >
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary text-sm min-w-[200px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Lock size={16} />
                  {isInFreeWindow() ? 'COMPLETE SIGN UP (FREE)' : 'SUBMIT & PAY R92'}
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}